<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { api, ServerError } from "@/api/server";
import EventSummary from "@/components/EventSummary.vue";
import TrendBars from "@/components/TrendBars.vue";
import type { ScreenTimeSeries } from "@/types/protocol";
import { byCategoryOrder, categoryColor, OTHER_COLOR, UNTRACKED } from "@/utils/categories";
import { formatDuration } from "@/utils/format";

/** Der Verlauf ist keine Live-Größe - er wird beim Laden und dann minütlich geholt. */
const REFRESH_MS = 60_000;

const ranges = [7, 14, 30] as const;
const days = ref<(typeof ranges)[number]>(14);
const series = ref<ScreenTimeSeries[]>([]);
const error = ref("");
const loading = ref(false);
/** Dieselben Zahlen als Tabelle - die Balken sollen nichts verstecken. */
const asTable = ref(false);

let timer: number | undefined;

const weekdayNames = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

/** Gemeinsame Skala über alle Benutzer, sonst täuschen die Balkenhöhen Vergleichbarkeit vor. */
const scaleSeconds = computed(() =>
  Math.max(1, ...series.value.flatMap((entry) => entry.days.map((day) => day.seconds))),
);

const rangeLabel = computed(() => {
  const first = series.value[0]?.days[0]?.date;
  return first ? `seit ${new Date(`${first}T00:00:00`).toLocaleDateString("de-DE")}` : "";
});

/** Spalten der Tabelle: die Kategorien des Benutzers, ggf. plus die nicht erfasste Zeit. */
function columns(entry: ScreenTimeSeries): string[] {
  const known = byCategoryOrder(entry.categories).map((category) => category.category);
  const untracked = entry.days.some(
    (day) => day.seconds - day.categories.reduce((sum, c) => sum + c.seconds, 0) > 1,
  );
  return untracked ? [...known, UNTRACKED] : known;
}

/** Sekunden einer Kategorie an einem Tag - `nicht erfasst` ist der Rest zur Tagessumme. */
function cell(day: ScreenTimeSeries["days"][number], column: string): number {
  if (column !== UNTRACKED)
    return day.categories.find((category) => category.category === column)?.seconds ?? 0;
  return Math.max(0, day.seconds - day.categories.reduce((sum, c) => sum + c.seconds, 0));
}

/** Stärkster Wochentag - die eine Zahl, die den Streifen beschriftet. */
function peak(entry: ScreenTimeSeries): ScreenTimeSeries["weekdays"][number] | undefined {
  const best = entry.weekdays.reduce<ScreenTimeSeries["weekdays"][number] | undefined>(
    (max, day) => (max === undefined || day.averageSeconds > max.averageSeconds ? day : max),
    undefined,
  );
  return best && best.averageSeconds > 0 ? best : undefined;
}

const dayLabel = (date: string) =>
  new Date(`${date}T00:00:00`).toLocaleDateString("de-DE", { weekday: "short", day: "numeric", month: "numeric" });

async function load(): Promise<void> {
  loading.value = true;
  try {
    series.value = await api.trend(days.value);
    error.value = "";
  } catch (cause) {
    // 401 behandelt der API-Layer (zurück zum Login) - hier nur melden.
    error.value = cause instanceof ServerError ? cause.message : String(cause);
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  void load();
  timer = window.setInterval(() => void load(), REFRESH_MS);
});

onBeforeUnmount(() => window.clearInterval(timer));

watch(days, () => void load());
</script>

<template>
  <section>
    <div class="head">
      <h2>Verlauf <span v-if="rangeLabel" class="range">{{ rangeLabel }}</span></h2>
      <div class="switch">
        <button
          v-for="range in ranges"
          :key="range"
          :class="{ on: days === range }"
          :disabled="loading"
          @click="days = range"
        >
          {{ range }} Tage
        </button>
        <button class="table-toggle" :class="{ on: asTable }" @click="asTable = !asTable">
          {{ asTable ? "Balken" : "Tabelle" }}
        </button>
      </div>
    </div>

    <EventSummary :days="days" />

    <p v-if="error" class="empty">{{ error }}</p>

    <div v-else-if="series.length" class="rows">
      <article v-for="entry in series" :key="entry.user" class="panel row">
        <div class="top">
          <span class="who">{{ entry.user }}</span>
          <span class="numbers">
            <b>{{ formatDuration(entry.totalSeconds) }}</b> gesamt ·
            Ø {{ formatDuration(entry.averageSeconds) }}/Tag
          </span>
        </div>

        <template v-if="!asTable">
          <TrendBars :days="entry.days" :scale-seconds="scaleSeconds" />

          <!-- Legende: trägt die Identität der Farben und gleich die Zahlen dazu. -->
          <div v-if="entry.categories.length" class="legend">
            <span v-for="category in byCategoryOrder(entry.categories)" :key="category.category">
              <i :style="{ background: categoryColor(category.category) }" />
              {{ category.category }}
              <b>{{ formatDuration(category.totalSeconds) }}</b>
              <!-- &nbsp;, weil Vue den Zeilenumbruch zwischen den Elementen verschluckt -->
              <span class="per-day">&nbsp;· Ø {{ formatDuration(category.averageSeconds) }}/Tag</span>
            </span>
          </div>

          <div class="footer">
            <div class="weekdays">
              <span class="caption">
                Ø je Wochentag <span class="note">(ohne heute)</span>
                <template v-if="peak(entry)">
                  · am meisten <b>{{ weekdayNames[peak(entry)!.weekday - 1] }}</b>
                  {{ formatDuration(peak(entry)!.averageSeconds) }}
                </template>
              </span>
              <div class="strip">
                <div
                  v-for="day in entry.weekdays"
                  :key="day.weekday"
                  class="weekday"
                  :title="`${weekdayNames[day.weekday - 1]}: Ø ${formatDuration(day.averageSeconds)} aus ${day.days} ${day.days === 1 ? 'Tag' : 'Tagen'}`"
                >
                  <div class="track">
                    <div
                      class="fill"
                      :style="{
                        height: `${(day.averageSeconds / Math.max(1, ...entry.weekdays.map((w) => w.averageSeconds))) * 100}%`,
                      }"
                    />
                  </div>
                  <span class="name">{{ weekdayNames[day.weekday - 1] }}</span>
                </div>
              </div>
            </div>

            <div v-if="entry.weeks.length > 1" class="weeks">
              <span class="caption">Wochensummen</span>
              <div v-for="week in entry.weeks" :key="week.from" class="week">
                <span class="kw">{{ week.week }}</span>
                <b>{{ formatDuration(week.totalSeconds) }}</b>
                <span class="per-day">
                  Ø {{ formatDuration(week.averageSeconds) }}/Tag<template v-if="week.days < 7">
                    · nur {{ week.days }} {{ week.days === 1 ? "Tag" : "Tage" }}</template>
                </span>
              </div>
            </div>
          </div>
        </template>

        <div v-else class="scroll">
          <table>
            <thead>
              <tr>
                <th>Tag</th>
                <th v-for="column in columns(entry)" :key="column">
                  <i :style="{ background: column === UNTRACKED ? OTHER_COLOR : categoryColor(column) }" />
                  {{ column }}
                </th>
                <th>Gesamt</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="day in entry.days" :key="day.date">
                <td class="day">{{ dayLabel(day.date) }}</td>
                <td v-for="column in columns(entry)" :key="column" class="num">
                  {{ cell(day, column) > 0 ? formatDuration(cell(day, column)) : "–" }}
                </td>
                <td class="num total">{{ day.seconds > 0 ? formatDuration(day.seconds) : "–" }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </article>
    </div>

    <div v-else class="empty">Noch keine Daten für diesen Zeitraum.</div>
  </section>
</template>

<style scoped>
.head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.range {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 400;
  opacity: 0.75;
}

.switch {
  display: flex;
  gap: 6px;
  margin-bottom: 10px;
}

.switch button {
  padding: 4px 10px;
  font-size: 13px;
}

.switch button.on {
  border-color: var(--accent);
  background: color-mix(in srgb, var(--accent) 25%, var(--panel2));
}

.table-toggle {
  margin-left: 6px;
}

.rows {
  display: grid;
  gap: 10px;
}

.row {
  padding: 12px 14px;
}

.top {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}

.who {
  font-weight: 600;
}

.numbers {
  color: var(--muted);
  font-size: 13px;
}

.numbers b {
  color: var(--fg);
  font-weight: 600;
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  margin-top: 10px;
  color: var(--muted);
  font-size: 12.5px;
}

.legend b {
  color: var(--fg);
  font-weight: 500;
}

.legend i,
th i {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 2px;
  margin-right: 5px;
}

.per-day {
  opacity: 0.75;
}

.footer {
  display: flex;
  flex-wrap: wrap;
  gap: 18px 32px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid var(--line);
  font-size: 12.5px;
  color: var(--muted);
}

.caption {
  display: block;
  margin-bottom: 6px;
  opacity: 0.8;
}

.note {
  opacity: 0.7;
}

.caption b {
  color: var(--fg);
  font-weight: 500;
}

.strip {
  display: flex;
  gap: 6px;
}

.weekday {
  display: grid;
  justify-items: center;
  gap: 3px;
}

.track {
  display: flex;
  align-items: flex-end;
  width: 16px;
  height: 26px;
  background: linear-gradient(var(--line), var(--line)) bottom / 100% 1px no-repeat;
}

.fill {
  width: 100%;
  border-radius: 3px 3px 0 0;
  background: var(--accent);
}

.name {
  font-size: 11.5px;
}

.weeks {
  display: grid;
  gap: 2px;
}

.week {
  display: flex;
  gap: 8px;
}

.week b {
  color: var(--fg);
  font-weight: 500;
}

.kw {
  min-width: 46px;
}

.scroll {
  overflow-x: auto;
  margin-top: 10px;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
}

th {
  text-align: right;
  font-weight: 500;
  color: var(--muted);
  padding: 4px 0 4px 12px;
  white-space: nowrap;
}

th:first-child {
  text-align: left;
  padding-left: 0;
}

td {
  padding: 3px 0 3px 12px;
  border-top: 1px solid var(--line);
  white-space: nowrap;
}

td:first-child {
  padding-left: 0;
}

.day {
  color: var(--muted);
}

.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.total {
  font-weight: 600;
}
</style>
